import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * Cart Drawer Component with MOV Progress Bar
 */
class CartDrawerComponent extends DialogComponent {
  #summaryThreshold = 0.5;
  #historyAbortController = null;

  // 🔥 CONFIG — change this based on your store
  #movThreshold = 999; // Free shipping threshold (₹)

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateMOVProgress);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateMOVProgress);

    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    this.#historyAbortController?.abort();
  }

  /* ================= HISTORY ================= */

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();

    window.addEventListener('popstate', this.#handlePopState, {
      signal: this.#historyAbortController.signal
    });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();

    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /* ================= CART EVENTS ================= */

  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    const cartCount = event.detail.resource?.item_count;
    const cartTotal = event.detail.resource?.total_price;

    this.#announceCartCount(cartCount);
    this.#updateMOVProgress(cartTotal);
  };

  #announceCartCount(cartCount) {
    const liveRegion = this.refs.liveRegion;

    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  /* ================= MOV PROGRESS ================= */

  #updateMOVProgress = (cartTotalFromEvent) => {
    const dialog = this.refs.dialog;
    if (!dialog) return;

    const progressBar = dialog.querySelector('[data-mov-progress]');
    const progressText = dialog.querySelector('[data-mov-text]');

    if (!progressBar || !progressText) return;

    // Shopify sends in paise → convert to ₹
    let cartTotal = cartTotalFromEvent;

    if (!cartTotal) {
      const totalEl = dialog.querySelector('[data-cart-total]');
      cartTotal = totalEl ? parseFloat(totalEl.dataset.cartTotal || '0') : 0;
    }

    cartTotal = cartTotal / 100;

    const remaining = this.#movThreshold - cartTotal;

    let progress = Math.min((cartTotal / this.#movThreshold) * 100, 100);

    // 🎯 Update UI
    progressBar.style.width = `${progress}%`;

    if (remaining > 0) {
      progressText.textContent = `Add ₹${Math.ceil(remaining)} more to get FREE delivery 🚚`;
    } else {
      progressText.textContent = `🎉 Yay! You unlocked FREE delivery`;
    }
  };

  /* ================= OPEN / CLOSE ================= */

  open() {
    this.showDialog();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  /* ================= STICKY SUMMARY ================= */

  #updateStickyState() {
    const { dialog } = this.refs;
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold ? 'false' : 'true'
    );
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}